import React, { CSSProperties, ReactNode, RefObject } from 'react';
import ReactMapGL, {
  AttributionControl,
  MapEvent,
  MapLayerMouseEvent,
  MapProps,
  MapProvider,
  MapRef,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl/maplibre';
// Static import: react-map-gl's maplibre entry defaults to a module-scope
// `import('maplibre-gl')` async chunk; passing mapLib explicitly keeps maplibre in the
// main bundle (same loading behavior as the previous static mapbox-gl import).
import maplibregl from 'maplibre-gl';

import { MapLoadIcon } from '@restworld/utils-common';

// OpenFreeMap vector tiles (OpenMapTiles schema), keyless and free. Loaded by URL rather
// than as a bundled style object: passing the style as an object to react-map-gl/maplibre
// in this build does not kick off the vector-tile requests (only the background renders),
// whereas the hosted URL loads reliably. Providers with the same schema (MapTiler,
// self-hosted PMTiles) are drop-in fallbacks by swapping this URL.
const TRUE = true;
export const DEFAULT_MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

// OpenFreeMap "Liberty" already ships the classic Mapbox Streets palette, but a bit flat.
// These paint overrides push it toward the Mapbox Streets v11 look we had before the
// migration (cyan water, more present greens, warmer secondary roads, more building
// contrast). Applied at runtime on map load so we keep the reliable URL-based tile
// loading; layers that are absent are skipped.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RESTWORLD_PAINT_OVERRIDES: Array<[string, string, any]> = [
  ['water', 'fill-color', '#a0c8f0'],
  ['park', 'fill-color', '#cfe6bf'],
  ['park', 'fill-opacity', 0.85],
  ['landcover_wood', 'fill-color', 'hsl(98,42%,66%)'],
  ['landcover_wood', 'fill-opacity', 0.55],
  ['landcover_grass', 'fill-color', 'rgb(190,220,165)'],
  ['landcover_grass', 'fill-opacity', 0.45],
  ['road_secondary_tertiary', 'line-color', '#fbe9a6'],
  ['road_trunk_primary', 'line-color', '#fbe9a6'],
  ['building', 'fill-color', 'hsl(35,10%,83%)'],
  // Continental/regional zoom: the land green is the Natural Earth shaded-relief raster
  // (gone by ~z6, so this does not touch the city views). Saturate and lift its opacity
  // so the greens read instead of looking washed out.
  ['natural_earth', 'raster-saturation', 0.5],
  ['natural_earth', 'raster-opacity', ['interpolate', ['exponential', 1.5], ['zoom'], 0, 0.75, 6, 0.22]],
];

const applyRestworldPalette = (map: maplibregl.Map) => {
  for (const [layer, prop, value] of RESTWORLD_PAINT_OVERRIDES) {
    if (map.getLayer(layer)) {
      try {
        map.setPaintProperty(layer, prop, value);
      } catch {
        // layer exists but property not applicable in this style version; skip
      }
    }
  }
};

export const MapWrapper = ({
  children,
  mapRef,
  coordinates,
  zoom,
  handleZoomEnd,
  mapStyle = DEFAULT_MAP_STYLE,
  initialViewState,
  touchZoomRotate = TRUE,
  cooperativeGestures = TRUE,
  onMove,
  onDrag,
  onResize,
  onZoom,
  style,
  interactiveLayerIds,
  onClick,
  viewState,
  reuseMaps = false,
  onLoad,
  scrollZoom = true,
  dragRotate = true,
  dragPan = true,
  doubleClickZoom = true,
  ...rest
}: MapWrapperProps) => {
  const handleLoad = (e: MapEvent) => {
    const map = e.target as maplibregl.Map;
    applyRestworldPalette(map);
    // maplibre renders the compact attribution EXPANDED by default (a full text bar) and does
    // not auto-collapse. Collapse it to just the "i" so on mobile the notice never sits over the
    // map. The attribution text (and its `maplibregl-compact-show` class) can be set AFTER the
    // `load` event, so collapse both now and once the map first goes idle. One-shot on idle, so
    // it never fights a user who later taps the "i" to read the credits (ODbL stays satisfied).
    const collapseAttribution = () => {
      try {
        map
          .getContainer()
          .querySelectorAll('.maplibregl-ctrl-attrib.maplibregl-compact-show')
          .forEach((el) => el.classList.remove('maplibregl-compact-show'));
      } catch {
        // cosmetic, non-fatal
      }
    };
    collapseAttribution();
    map.once('idle', collapseAttribution);
    onLoad?.(e);
  };
  return (
    <MapProvider>
      <ReactMapGLStyled
        ref={mapRef}
        mapLib={maplibregl}
        // No RTL scripts needed for an Italy-only product: the default would fetch the
        // rtl-text plugin from the Mapbox CDN on every map load.
        RTLTextPlugin=""
        initialViewState={initialViewState}
        onZoomEnd={handleZoomEnd}
        mapStyle={mapStyle}
        touchZoomRotate={touchZoomRotate}
        cooperativeGestures={cooperativeGestures}
        onMove={onMove}
        onDrag={onDrag}
        onResize={onResize}
        // OSM/OpenFreeMap attribution is required by the data license (ODbL). We render it
        // ourselves below (compact, bottom-left) instead of the default bottom-right control:
        // on mobile the bottom-right control group inherits some maps' `.maplibregl-ctrl-
        // bottom-right { position: fixed; bottom: 20% }` rule, which floated the expanded
        // notice into the middle of the map over the pins. Compact renders a small "i" that
        // expands on tap, so the notice never covers content. Still never hide it via CSS.
        attributionControl={false}
        style={style}
        interactiveLayerIds={interactiveLayerIds}
        onClick={onClick}
        onZoom={onZoom}
        reuseMaps={reuseMaps}
        viewState={viewState}
        onLoad={handleLoad}
        scrollZoom={scrollZoom}
        dragRotate={dragRotate}
        dragPan={dragPan}
        doubleClickZoom={doubleClickZoom}
        {...rest}
      >
        <AttributionControl compact position="bottom-left" />
        {children}
      </ReactMapGLStyled>
    </MapProvider>
  );
};
export default MapWrapper;
const ReactMapGLStyled = (props: React.ComponentProps<typeof ReactMapGL>) => (
  <ReactMapGL {...props} style={{ position: 'relative', ...props.style }} />
);
type MapEventHandlerType = (e: ViewStateChangeEvent) => void;
export interface MapWrapperProps {
  coordinates?: number[];
  children?: ReactNode;
  mapRef?: RefObject<MapRef | null>;
  zoom?: number;
  handleZoomEnd?: MapEventHandlerType;
  mapStyle?: string;
  touchZoomRotate?: boolean;
  cooperativeGestures?: boolean;
  longitude?: number;
  latitude?: number;
  onMove?: MapEventHandlerType;
  onDrag?: MapEventHandlerType;
  onZoom?: MapEventHandlerType;
  onResize?: (e: MapEvent) => void;
  style?: CSSProperties;
  initialViewState?: MapProps['initialViewState'];
  interactiveLayerIds?: string[];
  onClick?: (e: MapLayerMouseEvent) => void;
  viewState?: ViewState & {
    width: number;
    height: number;
  };
  reuseMaps?: boolean;
  onLoad?: (e: MapEvent) => void;
  scrollZoom?: boolean;
  dragRotate?: boolean;
  dragPan?: boolean;
  doubleClickZoom?: boolean;
}
export const MapScriptLoadingUI = () => (
  <div className="absolute w-full h-full bg-gray-200 flex justify-center items-center">
    <div className="flex flex-col gap-2 items-center w-1/2">
      <span>
        <MapLoadIcon />
      </span>
      <span className="text-gray-500 font-semibold text-lg line-clamp-2 text-center md:w-1/2">
        Stiamo facendo girare il mappamondo...
      </span>
    </div>
  </div>
);
